import { union, without } from 'es-toolkit';

import { forceArray } from '@paragon/utilities';

/**
 * Configuration type for recording visible objects with a whitelist / blacklist system.
 */
export type VisibilityConfiguration = 'all' | { visibleObjects: string[] } | { hiddenObjects: string[] };

/**
 * Utility functions for constructing and querying a `VisibilityConfiguration`.
 */
export const VisibilityConfig = {
  /**
   * Returns whether or not the given object GUID should be visible.
   */
  shouldBeVisible(guid: string | null, config: VisibilityConfiguration): boolean {
    if (config === 'all') return true;
    if (guid == null) return false;

    return 'visibleObjects' in config ? config.visibleObjects.includes(guid) : !config.hiddenObjects.includes(guid);
  },

  /**
   * Determines the visibility status of the given group of object GUIDs.
   */
  getVisibilityStatus(guids: string[], config: VisibilityConfiguration): 'all' | 'some' | 'none' {
    if (config === 'all') return 'all';

    const total = guids.length;
    const visibleCount = guids.filter((guid) => VisibilityConfig.shouldBeVisible(guid, config)).length;

    return visibleCount === total ? 'all' : visibleCount > 0 ? 'some' : 'none';
  },

  /**
   * Produces a predicate function to determine if a given object GUID is visible.
   */
  getFilterFn:
    (config: VisibilityConfiguration) =>
    (guid: string | null): boolean =>
      VisibilityConfig.shouldBeVisible(guid, config),

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Utility functions for creating new configurations.
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  all(): VisibilityConfiguration {
    return 'all';
  },

  none(): VisibilityConfiguration {
    return { visibleObjects: [] };
  },

  only(guid: string | string[]): VisibilityConfiguration {
    return { visibleObjects: forceArray(guid) };
  },

  allExceptFor(guid: string | string[]): VisibilityConfiguration {
    return { hiddenObjects: forceArray(guid) };
  },

  add(current: VisibilityConfiguration, additionalGuid: string): VisibilityConfiguration {
    return VisibilityConfig.concat(current, [additionalGuid]);
  },

  concat(current: VisibilityConfiguration, additionalGuids: string[]): VisibilityConfiguration {
    if (current === 'all') return 'all';

    if ('visibleObjects' in current) {
      return { visibleObjects: union(current.visibleObjects, additionalGuids) };
    } else {
      return { hiddenObjects: without(current.hiddenObjects, ...additionalGuids) };
    }
  },

  remove(current: VisibilityConfiguration, hiddenGuid: string): VisibilityConfiguration {
    if (current === 'all') {
      return VisibilityConfig.allExceptFor(hiddenGuid);
    }

    if ('visibleObjects' in current) {
      return { visibleObjects: without(current.visibleObjects, hiddenGuid) };
    } else {
      return { hiddenObjects: union(current.hiddenObjects, [hiddenGuid]) };
    }
  },

  toggle(current: VisibilityConfiguration, guid: string): VisibilityConfiguration {
    return VisibilityConfig.shouldBeVisible(guid, current)
      ? VisibilityConfig.remove(current, guid)
      : VisibilityConfig.add(current, guid);
  },
};
